import { useNavigator } from '@repo/toolkit/use-navigator'
import ActionButton from '@repo/ui/action-button'
import { Column } from '@repo/ui/data-table'
import { PenIcon, PlusIcon, TravelIcon, UploadIcon } from '@repo/ui/icons'
import React, { useState } from 'react'

import { API } from '../../api/apis.config'
import FilterDrawer from '../../components/elements/drawers/filter-drawer'
import DynamicDataTable, { DynamicTableRef } from '../../components/elements/dynamic-table'
import { countFilters } from '../../components/elements/dynamic-table/utils/countFilters'
import UserNavBar from '../../components/elements/nav-bar'
import { Paths } from '../../lib/constants/path'
import { useDownloadDocuments } from '../../lib/hooks/use-download-documents'
import { useFilters } from '../../lib/hooks/use-filters'
import { useModalManager } from '../../lib/hooks/use-modal-manager'
import {
  TripGrouping,
  TripGroupingFilters
} from '../../lib/types/models/trip-grouping/trip-grouping'
import {
  transformTripGroupingFiltersToUrl,
  transformUrlToTripGroupingFilters
} from '../../lib/utils/filters-mapping/trips-grouping'
import TripGroupingFilter from './trip-filter'
import { getTripGroupingColumns } from './utils'

const initialFilters: TripGroupingFilters = {
  years: [new Date().getFullYear()]
}

export function TripGroupingListPage() {
  const { navigate } = useNavigator()
  const { downloadDocuments } = useDownloadDocuments()
  const tableRef = React.useRef<DynamicTableRef<TripGrouping>>(null)
  const [isTableLoading, setIsTableLoading] = useState(false)

  const { selectedFilter, filter, onFilterChange, resetFilters, onSelectedFilterChange } =
    useFilters<TripGroupingFilters>({
      initialFilters,
      options: { allowSearchParam: true },
      transformFilterToUrl: transformTripGroupingFiltersToUrl,
      transformUrlToFilter: transformUrlToTripGroupingFilters
    })

  const { modalState, toggleModal, closeModel } = useModalManager<'filters', TripGrouping[]>([
    'filters'
  ])

  const columns: Column<TripGrouping>[] = [
    ...getTripGroupingColumns('SIMPLE'),
    {
      accessorKey: 'id',
      header: '',
      cell: ({ row }) => (
        <ActionButton
          variant={'grey'}
          icon={PenIcon}
          onClick={() =>
            navigate(Paths.TRIP_GROUPING_UPDATE_PAGE.replace(':id', row.original.id.toString()))
          }
        />
      )
    }
  ]

  return (
    <div>
      <UserNavBar title={'Groupes de voyage'} icon={TravelIcon} />
      <div className={'h-4'} />

      <DynamicDataTable<TripGrouping, TripGroupingFilters>
        ref={tableRef}
        onLoadingChange={setIsTableLoading}
        title={'Tous les groupes de voyages'}
        deleteMessage={{
          name: 'groupes de voyage',
          attributeName: 'name',
          deleteOne: (tripGrouping) =>
            `Êtes-vous sûr de vouloir supprimer ce groupe de voyage "${tripGrouping.name}"?`
        }}
        columns={columns}
        selectable
        endpoint={API.tripGroupings.getAll.url}
        onFiltersClick={() => toggleModal('filters')}
        filters={selectedFilter}
        filtersLength={countFilters(selectedFilter)}
        actions={[
          {
            type: 'global',
            icon: PlusIcon,
            variant: 'primary',
            label: 'Ajouter un nouveau groupe de voyages',
            fn: () => navigate(Paths.TRIP_GROUPING_CREATE_PAGE)
          },
          {
            type: 'multiple',
            icon: UploadIcon,
            refetch: true,
            variant: 'ghost',
            label: 'Exporter',
            fn: (tripGroupings) => {
              const ids = tripGroupings.map(({ id }) => id)
              downloadDocuments({ ids, endpoint: API.tripGroupings.exportExcel.url })
            }
          }
        ]}
      />

      {/* Filters Modal */}
      <FilterDrawer
        isOpen={modalState.filters}
        selectedFilter={selectedFilter}
        isLoading={isTableLoading}
        onClose={() => closeModel('filters')}
        onReset={resetFilters}
        onApply={() => onSelectedFilterChange({ ...filter })}
        onFilterChange={onFilterChange}
      >
        <TripGroupingFilter filter={filter} onFilterChange={onFilterChange} />
      </FilterDrawer>
    </div>
  )
}
